import type { FC, PropsWithChildren, ReactNode } from 'react';
import { useWatch } from 'react-hook-form';

import type { PostFormValues } from '@/features/post-editor/types/postForm';
import type { PostFeedType } from '@/features/posts/types/postFeed';

type Props = {
  /** The channel type(s) for which the children should render. */
  type: PostFeedType | PostFeedType[];
  /** Rendered when the channel does not match (defaults to nothing). */
  fallback?: ReactNode;
} & PropsWithChildren;

/**
 * Renders `children` only when the form's current channel type matches `type`
 * (a single channel or any-of a list). Reads the channel from the shared form
 * context, so callers don't need to watch or thread it through.
 */
export const PostChannelTypeGuard: FC<Props> = ({
  type,
  fallback,
  children
}) => {
  const channel = useWatch<PostFormValues, 'channelType'>({
    name: 'channelType'
  });

  const matches = Array.isArray(type)
    ? type.includes(channel)
    : channel === type;

  return <>{matches ? children : fallback}</>;
};
